<template>
  <div class="ur-heatmap">
    <!-- Toolbar -->
    <div class="ur-heatmap-toolbar">
      <button class="ur-lo-btn" @click="exportExcel">
        {{ $t('URBANRENEWAL_ADMIN.HEATMAP.EXPORT_EXCEL') }}
      </button>
    </div>

    <!-- Error bar -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ur-lo-loading">
      {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') }}
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Empty state -->
      <div v-if="gridRows.length === 0" class="ur-heatmap-empty">
        {{ projectId ? $t('URBANRENEWAL_ADMIN.HEATMAP.NO_DATA') : $t('URBANRENEWAL_ADMIN.HEATMAP.SELECT_PROJECT') }}
      </div>

      <!-- Grid -->
      <div v-else class="ur-heatmap-grid-wrap" ref="grid">
        <table class="ur-heatmap-table">
          <tbody>
            <tr v-for="row in gridRows" :key="row.floor">
              <!-- Floor label -->
              <td class="ur-heatmap-floor" :style="FLOOR_STYLE">{{ row.floor }}</td>
              <!-- Unit cells -->
              <td
                v-for="cell in row.cells"
                :key="cell.unit_code"
                :style="cellStyle(cell)"
                @mouseenter="onCellEnter(cell, $event)"
                @mousemove="onCellMove"
                @mouseleave="onCellLeave"
              >
                <div class="ur-heatmap-code">{{ cell.unit }}</div>
                <div class="ur-heatmap-count">
                  {{ cell.selection_count }} {{ $t('URBANRENEWAL_ADMIN.HEATMAP.UNIT_GROUP') }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Legend -->
      <div class="ur-heatmap-legend">
        <div class="ur-heatmap-legend-item">
          <span class="ur-heatmap-legend-dot" style="background:#FFFFFF;border:1px solid #888888;"></span>
          {{ $t('URBANRENEWAL_ADMIN.HEATMAP.LGD_NONE') }}
        </div>
        <div class="ur-heatmap-legend-item">
          <span class="ur-heatmap-legend-dot" style="background:#bde3f5;border:1px solid #1A6894;"></span>
          {{ $t('URBANRENEWAL_ADMIN.HEATMAP.LGD_ONE') }}
        </div>
        <div class="ur-heatmap-legend-item">
          <span class="ur-heatmap-legend-dot" style="background:#f4bec2;border:1px solid #922B21;"></span>
          {{ $t('URBANRENEWAL_ADMIN.HEATMAP.LGD_MULTI') }}
        </div>
      </div>
    </template>

    <!-- Tooltip (fixed, pointer-events: none) -->
    <div
      v-show="hover.visible"
      class="ur-heatmap-tooltip"
      :style="tooltipStyle"
    >
      <template v-if="hover.info">
        <div><span class="ur-tt-label">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_UNIT_CODE') }}：</span>{{ hover.info.unit_code }}</div>
        <div><span class="ur-tt-label">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_AREA') }}：</span>{{ hover.info.area_ping }}{{ $t('URBANRENEWAL_ADMIN.HEATMAP.U_PING') }}</div>
        <div><span class="ur-tt-label">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_UNIT_PRICE') }}：</span>{{ fmt(hover.info.unit_price) }}{{ $t('URBANRENEWAL_ADMIN.HEATMAP.U_YUAN') }}</div>
        <div><span class="ur-tt-label">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_TOTAL_PRICE') }}：</span>{{ fmt(hover.info.total_price) }}{{ $t('URBANRENEWAL_ADMIN.HEATMAP.U_YUAN') }}</div>
        <div>
          <span class="ur-tt-label">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_COUNT') }}：</span>{{ hover.info.selection_count }}{{ $t('URBANRENEWAL_ADMIN.HEATMAP.UNIT_GROUP') }}{{ wrap(hover.info.groups) }}
        </div>
        <div v-if="hasCategory" class="ur-tt-cat">
          <span v-if="hover.info.normal_owners && hover.info.normal_owners.length">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_NORMAL') }}：{{ wrap(hover.info.normal_owners) }} </span>
          <span v-if="hover.info.non_priority_owners && hover.info.non_priority_owners.length">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_NONPRIORITY') }}：{{ wrap(hover.info.non_priority_owners) }} </span>
          <span v-if="hover.info.over_owners && hover.info.over_owners.length">{{ $t('URBANRENEWAL_ADMIN.HEATMAP.TT_OVER') }}：{{ wrap(hover.info.over_owners) }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';

const CELL = {
  none:  { bg: '#FFFFFF', text: '#888888' },
  one:   { bg: '#bde3f5', text: '#1A6894' },
  multi: { bg: '#f4bec2', text: '#922B21' },
};
const CELL_BORDER = '#e0e0e0';
// 樓層標籤格用 inline style，確保 PDF 列印視窗（clone outerHTML）保留底色
const FLOOR_STYLE = {
  background: '#f4f4f4',
  border: '1px solid #e0e0e0',
  fontWeight: '600',
  color: '#444',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  padding: '6px 14px',
};

export default {
  name: 'HousingHeatmap',

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      heatmap: [],
      loading: false,
      errorMsg: '',
      FLOOR_STYLE: FLOOR_STYLE,
      hover: {
        visible: false,
        info: null,
        x: 0,
        y: 0,
      },
    };
  },

  computed: {
    tooltipStyle() {
      // 邊界翻轉：游標近右/下緣時，tooltip 改置於左/上，避免溢出視窗
      const TW = 240;
      const TH = 180;
      const pad = 8;
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      let left = this.hover.x + pad;
      let top = this.hover.y + pad;
      if (left + TW > winW) left = this.hover.x - TW - pad;
      if (top + TH > winH) top = this.hover.y - TH - pad;
      if (left < pad) left = pad;
      if (top < pad) top = pad;
      return { left: left + 'px', top: top + 'px' };
    },

    hasCategory() {
      const i = this.hover.info;
      return !!i && (
        (i.normal_owners && i.normal_owners.length) ||
        (i.non_priority_owners && i.non_priority_owners.length) ||
        (i.over_owners && i.over_owners.length)
      );
    },

    gridRows() {
      const floorMap = new Map();

      this.heatmap.forEach(function(h) {
        const code = h.unit_code;
        const dash = code.indexOf('-');
        let floor, unit;
        if (dash === -1) {
          floor = code;
          unit = code;
        } else {
          floor = h.floor || code.slice(0, dash);
          unit = code.slice(dash + 1);
        }
        const cell = Object.assign({}, h, { unit: unit });
        if (!floorMap.has(floor)) {
          floorMap.set(floor, []);
        }
        floorMap.get(floor).push(cell);
      });

      // Sort floors: numeric descending; NaN (non-numeric) at end
      const floors = Array.from(floorMap.keys());
      floors.sort(function(a, b) {
        const na = parseInt(a, 10);
        const nb = parseInt(b, 10);
        const aIsNaN = isNaN(na);
        const bIsNaN = isNaN(nb);
        if (aIsNaN && bIsNaN) return a.localeCompare(b);
        if (aIsNaN) return 1;
        if (bIsNaN) return -1;
        return nb - na;
      });

      return floors.map(function(floor) {
        const cells = floorMap.get(floor).slice().sort(function(a, b) {
          const re = /([A-Za-z]+)(\d+)/;
          const ma = re.exec(a.unit);
          const mb = re.exec(b.unit);
          if (ma && mb) {
            const cmp = ma[1].localeCompare(mb[1]);
            if (cmp !== 0) return cmp;
            return parseInt(ma[2], 10) - parseInt(mb[2], 10);
          }
          return a.unit.localeCompare(b.unit);
        });
        return { floor: floor, cells: cells };
      });
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },

  methods: {
    cellStyle(cell) {
      let colorScheme;
      if (cell.selection_count === 0) {
        colorScheme = CELL.none;
      } else if (cell.selection_count === 1) {
        colorScheme = CELL.one;
      } else {
        colorScheme = CELL.multi;
      }

      return {
        backgroundColor: colorScheme.bg,
        color: colorScheme.text,
        border: '1px solid ' + CELL_BORDER,
        padding: '6px 8px',
        textAlign: 'center',
        minWidth: '84px',
        cursor: 'default',
        verticalAlign: 'middle',
        transition: 'filter 100ms',
      };
    },

    async load() {
      if (!this.projectId) {
        this.heatmap = [];
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      try {
        const res = await UrbanRenewalDataService.getHousingHeatmap(this.projectId);
        this.heatmap = res.data;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.HEATMAP.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    onCellEnter(cell, e) {
      // 設計稿：hover 時整格加亮度遮罩
      if (e.currentTarget) e.currentTarget.style.filter = 'brightness(0.94)';
      this.hover = { visible: true, info: cell, x: e.clientX, y: e.clientY };
    },

    onCellMove(e) {
      if (this.hover.visible) {
        this.hover.x = e.clientX;
        this.hover.y = e.clientY;
      }
    },

    onCellLeave(e) {
      if (e && e.currentTarget) e.currentTarget.style.filter = '';
      this.hover.visible = false;
    },

    // 姓名清單 → 「（甲）（乙）」；空清單回傳空字串
    wrap(arr) {
      return arr && arr.length ? arr.map(function(n) { return '（' + n + '）'; }).join('') : '';
    },

    fmt(v) {
      // 設計稿：千分位、無小數
      return v == null ? '-' : Number(v).toLocaleString('zh-TW');
    },

    async exportExcel() {
      this.errorMsg = '';
      if (!this.projectId) return;
      try {
        const res = await UrbanRenewalDataService.exportHousingHeatmap(this.projectId);
        this.downloadBlob(res, '選配狀況_房屋.xlsx');
      } catch (e) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.HEATMAP.EXPORT_FAILED');
      }
    },

    downloadBlob(res, fallbackName) {
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let fn = fallbackName;
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
        if (m) fn = decodeURIComponent(m[1]);
      }
      a.download = fn;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>
